import React, { useEffect, useState } from 'react';
import { getInstallState, onInstallStateChange, promptInstall } from '../utils/pwa';

/**
 * InstallAppBanner
 *
 * Floating bottom banner that nudges sellers to install the seller portal
 * as a real app. Renders nothing when:
 *   - already installed (display-mode: standalone, or appinstalled flag set)
 *   - the user explicitly dismissed it (stored in localStorage for 14 days)
 *   - we have no install signal AND the device is desktop (where the
 *     browser already shows its own install affordance in the URL bar)
 *
 * iOS Safari has no programmatic prompt API, so we show a brief
 * "Tap Share → Add to Home Screen" instruction block instead.
 */
const DISMISS_KEY = 'aniyor.pwa.dismissed_at';
const DISMISS_DAYS = 14;

export default function InstallAppBanner() {
  const [state, setState] = useState(getInstallState());
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const off = onInstallStateChange(setState);
    try {
      const at = localStorage.getItem(DISMISS_KEY);
      if (at) {
        const ageDays = (Date.now() - Number(at)) / 86400_000;
        if (ageDays < DISMISS_DAYS) setDismissed(true);
      }
    } catch (_) { /* ignore */ }
    return off;
  }, []);

  if (state.installed) return null;
  if (dismissed) return null;
  // Only surface on mobile — desktop browsers show their own affordance
  if (!state.isIOS && !state.isAndroid) return null;
  // Android: need the captured prompt
  if (state.isAndroid && !state.installable) return null;

  const onInstall = async () => {
    if (state.isIOS) {
      // No prompt API — toggle a help sheet
      setState((s) => ({ ...s, _showIOSHelp: true }));
      return;
    }
    await promptInstall();
  };

  const onDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div
      data-testid="install-app-banner"
      style={{
        position: 'fixed',
        bottom: 'max(env(safe-area-inset-bottom), 16px)',
        left: 16,
        right: 16,
        zIndex: 9000,
        background: '#BA2D1B',
        color: '#fff',
        borderRadius: 14,
        padding: '14px 16px',
        boxShadow: '0 12px 40px rgba(186, 45, 27, 0.45)',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        maxWidth: 560,
        margin: '0 auto',
        fontFamily: 'inherit',
      }}
    >
      <img
        src="/logo192.png"
        alt=""
        width={42}
        height={42}
        style={{ borderRadius: 10, flexShrink: 0 }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 14, lineHeight: 1.2 }}>
          Install Aniyor Seller
        </div>
        <div style={{ fontSize: 12, opacity: 0.75, marginTop: 2 }}>
          {state.isIOS
            ? 'Get order alerts on your home screen — works like a real app.'
            : 'Add to home screen for instant new-order alerts with sound.'}
        </div>
        {state._showIOSHelp && (
          <div
            style={{
              fontSize: 12,
              marginTop: 8,
              padding: 10,
              background: 'rgba(255,255,255,0.08)',
              borderRadius: 8,
              lineHeight: 1.5,
            }}
          >
            1. Tap the <strong>Share</strong> icon in Safari<br />
            2. Choose <strong>Add to Home Screen</strong><br />
            3. Open Aniyor from your home screen → notifications turn on.
          </div>
        )}
      </div>
      {!state._showIOSHelp && (
        <button
          data-testid="install-app-cta"
          onClick={onInstall}
          style={{
            background: '#E46B2C',
            color: 'white',
            border: 0,
            padding: '10px 14px',
            borderRadius: 10,
            fontWeight: 700,
            fontSize: 13,
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          {state.isIOS ? 'How' : 'Install'}
        </button>
      )}
      <button
        data-testid="install-app-dismiss"
        onClick={onDismiss}
        aria-label="Dismiss install prompt"
        style={{
          background: 'transparent',
          color: 'rgba(255,255,255,0.6)',
          border: 0,
          padding: 6,
          cursor: 'pointer',
          fontSize: 18,
          lineHeight: 1,
          flexShrink: 0,
        }}
      >
        ×
      </button>
    </div>
  );
}
